// File: frontend/src/context/CookieConsentContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { applyConsent } from "../utils/analytics";

/**
 * Cookie consent state, persisted in localStorage.
 *
 * Why localStorage and not a cookie: the platform writes no cookies of its own
 * (see the Cookie Policy). Storing the choice the same way keeps that claim true.
 *
 * The stored record carries the policy version it was given under. When the
 * admin bumps the Cookie Policy version after a material change, the stored
 * choice no longer matches and the bar is shown again — which is exactly what
 * the policy text promises.
 */

const STORAGE_KEY = "cookieConsent";

export interface CookieConsent {
    /** Always true — required for the platform to function, cannot be declined. */
    necessary: boolean;
    /** Traffic measurement (GA4). */
    analytics: boolean;
    /** Personalised advertising. */
    ads: boolean;
}

interface StoredConsent extends CookieConsent {
    /** Cookie Policy version this choice was made under. */
    policyVersion: string;
    /** ISO timestamp of the decision, kept so the choice can be evidenced. */
    decidedAt: string;
}

/** Nothing optional runs until the visitor actively says otherwise. */
export const DENIED_CONSENT: CookieConsent = {
    necessary: true,
    analytics: false,
    ads: false,
};

export const GRANTED_CONSENT: CookieConsent = {
    necessary: true,
    analytics: true,
    ads: true,
};

interface CookieConsentContextType {
    consent: CookieConsent;
    /** True when no valid decision is stored yet — the bar should be shown. */
    needsDecision: boolean;
    /** True while the preferences dialog is open. */
    preferencesOpen: boolean;
    acceptAll: () => void;
    rejectAll: () => void;
    savePreferences: (consent: CookieConsent) => void;
    openPreferences: () => void;
    closePreferences: () => void;
    /** Called once the Cookie Policy version is known, to invalidate stale choices. */
    setPolicyVersion: (version: string) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType>({
    consent: DENIED_CONSENT,
    needsDecision: false,
    preferencesOpen: false,
    acceptAll: () => {},
    rejectAll: () => {},
    savePreferences: () => {},
    openPreferences: () => {},
    closePreferences: () => {},
    setPolicyVersion: () => {},
});

/** Read the stored decision, tolerating a corrupted or unavailable store. */
const readStored = (): StoredConsent | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredConsent;
        // A record without the boolean flags is not a decision we can honour
        if (typeof parsed.analytics !== "boolean" || typeof parsed.ads !== "boolean") return null;
        return parsed;
    } catch {
        // Private mode or malformed JSON — treat as "no decision yet"
        return null;
    }
};

interface CookieConsentProviderProps {
    children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
    // Lazy initializers so localStorage is read once at mount rather than on
    // every render of a provider that wraps the whole application
    const [consent, setConsent] = useState<CookieConsent>(() => {
        const stored = readStored();
        return stored
            ? { necessary: true, analytics: stored.analytics, ads: stored.ads }
            : DENIED_CONSENT;
    });
    const [decided, setDecided] = useState<boolean>(() => readStored() !== null);
    const [storedVersion, setStoredVersion] = useState<string>(() => readStored()?.policyVersion ?? "");
    const [policyVersion, setPolicyVersionState] = useState<string>("");
    const [preferencesOpen, setPreferencesOpen] = useState(false);

    // Apply the decision to the actual scripts on every change, including the
    // initial mount. This is the single place where consent turns into behaviour.
    //
    // Under Consent Mode the tag is already running in the denied state set in
    // public/index.html, so this call is an update rather than a switch-on. On
    // first mount it replays a stored decision; for a visitor who has not
    // decided yet it re-states "denied", which is harmless and keeps the tag's
    // state explicit rather than relying on the default timing out.
    useEffect(() => {
        applyConsent(consent.analytics, consent.ads);
    }, [consent.analytics, consent.ads]);

    /**
     * A stored choice made under an older policy version is no longer a valid
     * consent for the current text, so the visitor is asked again.
     */
    const setPolicyVersion = useCallback((version: string) => {
        setPolicyVersionState(version);
    }, []);

    const persist = useCallback((next: CookieConsent) => {
        const record: StoredConsent = {
            ...next,
            necessary: true,
            policyVersion: policyVersion,
            decidedAt: new Date().toISOString(),
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        } catch {
            // Storage unavailable — the choice still applies for this session,
            // and the bar simply reappears on the next visit
        }
        setConsent({ ...next, necessary: true });
        setStoredVersion(policyVersion);
        setDecided(true);
        setPreferencesOpen(false);
    }, [policyVersion]);

    const acceptAll        = useCallback(() => persist(GRANTED_CONSENT), [persist]);
    const rejectAll        = useCallback(() => persist(DENIED_CONSENT), [persist]);
    const savePreferences  = useCallback((next: CookieConsent) => persist(next), [persist]);
    const openPreferences  = useCallback(() => setPreferencesOpen(true), []);
    const closePreferences = useCallback(() => setPreferencesOpen(false), []);

    // Ask again when there is no decision at all, or when the decision predates
    // the current policy version. An empty policyVersion means the document has
    // not loaded yet, in which case a stale-version check would be premature.
    const needsDecision =
        !decided || (policyVersion !== "" && storedVersion !== policyVersion);

    return (
        <CookieConsentContext.Provider
            value={{
                consent,
                needsDecision,
                preferencesOpen,
                acceptAll,
                rejectAll,
                savePreferences,
                openPreferences,
                closePreferences,
                setPolicyVersion,
            }}
        >
            {children}
        </CookieConsentContext.Provider>
    );
};

/**
 * Hook to read and change the cookie decision.
 * The footer uses openPreferences() so the choice is always reachable, as the
 * Cookie Policy promises.
 */
export const useCookieConsent = (): CookieConsentContextType =>
    useContext(CookieConsentContext);